/* ============================================================
   BULK HUMAN HAIR v2 — Mobile-first product page logic
   - Shade + density selection → variant resolution
   - Price updates (main + ATC + sticky)
   - Sticky ATC reveal on scroll past main ATC
   - Gallery scroll dots
   - Cart add via fetch (no full page nav)
   ============================================================ */
(function () {
  'use strict';

  function init(section) {
    const productId = section.dataset.bhhProductId;
    const currency = section.dataset.bhhCurrency || '$';

    // ===== State =====
    let selectedShade = null;
    let selectedDensity = null;
    let currentVariantId = null;

    // ===== Elements =====
    const shadeBlock = section.querySelector('[data-bhh-shade-block]');
    const shadeChips = section.querySelectorAll('[data-bhh-shade-value]');
    const shadeName = section.querySelector('[data-bhh-shade-name]');
    const densityCards = section.querySelectorAll('[data-bhh-density-value]');
    const priceEl = section.querySelector('[data-bhh-price]');
    const atcPrice = section.querySelector('[data-bhh-atc-price]');
    const stickyPrice = section.querySelector('[data-bhh-sticky-price]');
    const variantInput = section.querySelector('[data-bhh-variant-input]');
    const qtyInput = section.querySelector('[data-bhh-qty-input]');
    const qtyHidden = section.querySelector('[data-bhh-qty-hidden]');
    const qtyIncr = section.querySelector('[data-bhh-qty-incr]');
    const qtyDecr = section.querySelector('[data-bhh-qty-decr]');
    const form = section.querySelector('[data-bhh-form]');
    const atcBtn = section.querySelector('[data-bhh-atc]');
    const sticky = section.querySelector('[data-bhh-sticky]');
    const stickyAtc = section.querySelector('[data-bhh-sticky-atc]');
    const gallery = section.querySelector('[data-bhh-gallery]');
    const dots = section.querySelectorAll('[data-bhh-dots] .bhh-gallery__dot');

    // Initial selection from currently-selected variant
    selectedShade = section.querySelector('.bhh-shade__chip.is-selected')?.dataset.bhhShadeValue || null;
    selectedDensity = section.querySelector('.bhh-density__card.is-selected')?.dataset.bhhDensityValue || null;
    currentVariantId = variantInput?.value || null;

    // ===== Variant resolution =====
    // Fetch product JSON once for variant lookup
    let productData = null;
    fetch(`/products/${getProductHandle()}.js`)
      .then(r => r.json())
      .then(d => { productData = d; updateVariant(); })
      .catch(() => {});

    function getProductHandle() {
      const path = window.location.pathname;
      const m = path.match(/\/products\/([^/?#]+)/);
      return m ? m[1] : '';
    }

    function findVariant() {
      if (!productData) return null;
      return productData.variants.find(v => {
        const matchShade = !selectedShade || v.option1 === selectedShade || v.options[0] === selectedShade;
        const matchDensity = !selectedDensity ||
          v.option2 === selectedDensity ||
          v.option1 === selectedDensity ||
          v.options.includes(selectedDensity);
        return matchShade && matchDensity && v.available !== false;
      });
    }

    function fmtMoney(cents) {
      const dollars = (cents / 100).toFixed(2);
      return currency + dollars;
    }

    function updateVariant() {
      const v = findVariant();
      if (!v) {
        if (atcBtn) {
          atcBtn.textContent = 'UNAVAILABLE';
          atcBtn.disabled = true;
        }
        return;
      }
      currentVariantId = v.id;
      if (variantInput) variantInput.value = v.id;
      if (priceEl) priceEl.textContent = fmtMoney(v.price);
      if (atcPrice) atcPrice.textContent = fmtMoney(v.price);
      if (stickyPrice) stickyPrice.textContent = fmtMoney(v.price);
      if (atcBtn) {
        atcBtn.disabled = false;
        // Reset textContent without losing the price span
        atcBtn.innerHTML = 'ADD TO CART · <span data-bhh-atc-price>' + fmtMoney(v.price) + '</span>';
      }
      // Update URL silently (best practice for shareability without history pollution)
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}
    }

    // ===== Shade chip clicks =====
    shadeChips.forEach(chip => {
      chip.addEventListener('click', () => {
        if (chip.classList.contains('is-unavailable')) return;
        shadeChips.forEach(c => {
          c.classList.remove('is-selected');
          c.setAttribute('aria-checked', 'false');
        });
        chip.classList.add('is-selected');
        chip.setAttribute('aria-checked', 'true');
        selectedShade = chip.dataset.bhhShadeValue;
        if (shadeName) shadeName.textContent = selectedShade;
        updateVariant();
      });
    });

    // ===== Density card clicks =====
    densityCards.forEach(card => {
      card.addEventListener('click', () => {
        densityCards.forEach(c => {
          c.classList.remove('is-selected');
          c.setAttribute('aria-checked', 'false');
        });
        card.classList.add('is-selected');
        card.setAttribute('aria-checked', 'true');
        selectedDensity = card.dataset.bhhDensityValue;
        updateVariant();
      });
    });

    // ===== Qty stepper =====
    function syncQty() {
      const v = Math.max(1, Math.min(99, parseInt(qtyInput.value, 10) || 1));
      qtyInput.value = v;
      if (qtyHidden) qtyHidden.value = v;
    }
    qtyIncr?.addEventListener('click', () => {
      qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1;
      syncQty();
    });
    qtyDecr?.addEventListener('click', () => {
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
      syncQty();
    });
    qtyInput?.addEventListener('input', syncQty);

    // ===== Sticky ATC reveal =====
    if (sticky && atcBtn) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          // Show sticky when main ATC is NOT visible
          if (entry.isIntersecting) {
            sticky.hidden = true;
          } else {
            // Only show if we've scrolled past the main ATC (entering below)
            sticky.hidden = entry.boundingClientRect.top > 0;
          }
        });
      }, { threshold: 0, rootMargin: '0px 0px -80px 0px' });
      observer.observe(atcBtn);
    }

    // Sticky button delegates to main form
    stickyAtc?.addEventListener('click', () => {
      form?.requestSubmit?.();
    });

    // ===== Cart add via fetch (smoother UX) =====
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!currentVariantId) return;
      atcBtn.disabled = true;
      const originalText = atcBtn.innerHTML;
      atcBtn.textContent = 'ADDING…';
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(currentVariantId, 10), quantity: parseInt(qtyInput.value, 10) || 1 }]
          })
        });
        if (!res.ok) throw new Error('add failed');
        atcBtn.textContent = 'ADDED ✓';
        // Fire a cart update event for cart drawer / count UIs
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        setTimeout(() => {
          atcBtn.innerHTML = originalText;
          atcBtn.disabled = false;
          // Open the cart drawer if one exists; otherwise navigate
          const drawerTrigger = document.querySelector('[data-cart-drawer-open], .cart-drawer-toggle, .header__cart-toggle');
          if (drawerTrigger) {
            drawerTrigger.click();
          } else {
            window.location.href = '/cart';
          }
        }, 600);
      } catch (err) {
        atcBtn.textContent = 'ERROR · TRY AGAIN';
        setTimeout(() => {
          atcBtn.innerHTML = originalText;
          atcBtn.disabled = false;
        }, 1200);
      }
    });

    // ===== Gallery scroll → dots =====
    if (gallery && dots.length > 0) {
      let scrollTimer = null;
      gallery.addEventListener('scroll', () => {
        if (scrollTimer) cancelAnimationFrame(scrollTimer);
        scrollTimer = requestAnimationFrame(() => {
          const slideWidth = gallery.offsetWidth;
          const idx = Math.round(gallery.scrollLeft / slideWidth);
          dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
        });
      });
    }
  }

  // Boot — init each section
  function boot() {
    document.querySelectorAll('[data-bhh-section]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
